import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme/ThemeProvider';
import { Check, ArrowRight, ChevronDown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FadeUp, BlurIn, FadeLeft, FadeRight, StaggerContainer, StaggerItem } from '../components/ScrollReveal';

function FAQ({ question, answer }) {
  const { t } = useTheme();
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 14, overflow: 'hidden' }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'none', border: 'none', color: t.text, cursor: 'pointer', fontSize: 14, fontWeight: 600, textAlign: 'left',
      }}>
        {question}
        <ChevronDown size={16} color={t.textDim} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ padding: '0 20px 16px', fontSize: 13, color: t.textMuted, lineHeight: 1.6 }}>{answer}</div>
      )}
    </div>
  );
}

export default function PricingPage() {
  const { t, glassCard, iri } = useTheme();

  const checkStyle = { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: t.textMuted, padding: '6px 0' };

  const proFeatures = [
    '1 Telegram channel',
    'Standard execution speed',
    'Basic signal history (50 signals)',
    'Dashboard access',
    'Email support',
  ];

  const maxFeatures = [
    'Unlimited Telegram channels',
    'Priority execution speed',
    'Full trade history + Excel export',
    'Dashboard + Advanced analytics',
    'Priority support',
    'Multi-account MT5 support',
    'Custom lot sizing per channel',
    'Webhook integrations (coming soon)',
  ];

  return (
    <>
      <Navbar />
      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* ── Header ── */}
        <section style={{ maxWidth: 1280, margin: '0 auto', padding: '80px 24px 60px', textAlign: 'center' }}>
          <BlurIn>
            <h1 className="iri-text page-title" style={{ fontSize: 42, fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 14 }}>Simple, transparent pricing</h1>
          </BlurIn>
          <FadeUp delay={0.15} y={20}>
            <p style={{ fontSize: 16, color: t.textMuted }}>Choose the plan that fits your trading</p>
          </FadeUp>
        </section>

        {/* ── Pricing Cards ── */}
        <section style={{ maxWidth: 840, margin: '0 auto', padding: '0 24px 80px' }}>
          <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

            {/* Pro */}
            <FadeLeft delay={0.1}>
              <div style={{ ...glassCard, padding: 36, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{
                  display: 'inline-block', alignSelf: 'flex-start', marginBottom: 20,
                  background: t.badgeBuyBg, color: t.badgeBuyText, border: `1px solid ${t.badgeBuyBorder}`,
                  borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
                }}>PRO</div>

                <div style={{ marginBottom: 24 }}>
                  <span className="price-tag" style={{ fontSize: 42, fontWeight: 900, letterSpacing: '-0.03em' }}>$29</span>
                  <span style={{ fontSize: 14, color: t.textDim, fontWeight: 500 }}>/mo</span>
                </div>

                <div style={{ height: 1, background: t.divider, marginBottom: 24 }} />

                <div style={{ flex: 1, marginBottom: 28 }}>
                  {proFeatures.map((f, i) => (
                    <div key={i} style={checkStyle}>
                      <Check size={15} color={t.positive} />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <Link to="/login" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                  padding: '12px 0', borderRadius: 12, fontSize: 14, fontWeight: 700,
                  background: t.primaryBg, border: `1px solid ${t.primaryBorder}`, color: t.primaryText,
                  textDecoration: 'none', transition: 'all 0.2s',
                }}>
                  Get Started
                </Link>
              </div>
            </FadeLeft>

            {/* Max */}
            <FadeRight delay={0.2}>
              <div className="iri-border" style={{ ...glassCard, padding: 36, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%' }}>
                {/* Recommended badge */}
                <div style={{
                  position: 'absolute', top: -12, right: 24,
                  background: iri, color: '#fff',
                  borderRadius: 8, padding: '4px 14px', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
                }}>RECOMMENDED</div>

                <div style={{
                  display: 'inline-block', alignSelf: 'flex-start', marginBottom: 20,
                  background: 'linear-gradient(135deg, rgba(167,139,250,0.2), rgba(96,165,250,0.2))',
                  border: `1px solid rgba(167,139,250,0.3)`,
                  borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
                }}>
                  <span className="iri-text">MAX</span>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <span className="price-tag" style={{ fontSize: 42, fontWeight: 900, letterSpacing: '-0.03em' }}>$79</span>
                  <span style={{ fontSize: 14, color: t.textDim, fontWeight: 500 }}>/mo</span>
                </div>

                <div style={{ height: 1, background: t.divider, marginBottom: 24 }} />

                <div style={{ flex: 1, marginBottom: 28 }}>
                  {maxFeatures.map((f, i) => (
                    <div key={i} style={checkStyle}>
                      <Check size={15} color={t.positive} />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <Link to="/login" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                  padding: '12px 0', borderRadius: 12, fontSize: 14, fontWeight: 700,
                  background: iri, color: '#fff',
                  border: 'none', textDecoration: 'none', transition: 'all 0.2s',
                  boxShadow: '0 14px 30px rgba(111,168,255,0.20)',
                }}>
                  Get Started <ArrowRight size={15} />
                </Link>
              </div>
            </FadeRight>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px 60px' }}>
          <FadeUp>
            <h2 style={{ fontSize: 22, fontWeight: 800, textAlign: 'center', marginBottom: 28 }}>Frequently Asked Questions</h2>
          </FadeUp>
          <StaggerContainer stagger={0.08} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { q: 'Can I switch plans?', a: 'Yeah, you can upgrade from Pro to Max whenever you want. You only pay the difference for the rest of your billing cycle.' },
              { q: 'Is there a free trial?', a: 'New accounts get a 14-day free trial on Pro. No credit card needed.' },
              { q: 'What payment methods work?', a: 'Credit cards, Apple Pay, Google Pay, and bank transfers through Stripe.' },
              { q: 'Can I cancel anytime?', a: 'Yep. Cancel from your dashboard and you keep access until the end of your billing period.' },
            ].map((faq, i) => (
              <StaggerItem key={i} y={15}>
                <FAQ question={faq.q} answer={faq.a} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </section>

        <Footer />
      </div>
    </>
  );
}
